import {match} from 'ts-pattern';

import {always, flatten, isEmpty, isNil, isNotNil, uniqBy} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {omneticApi} from '@dms/api/base';
import {
  GetAllApiEnumsApiArg,
  GetAllApiEnumsApiResponse,
  GetAllMakeModelsApiArgs,
  GetAllMakeModelsApiResponse,
  GetApiMakeModelsWithMakeApiArg,
  GetApiMakeModelWithMakeApiArg,
  GetApiMakeModelWithMakeApiResponse,
  Makes,
  ModelFamilies,
  Models,
} from '@dms/api/shared';

import {
  GetCustomTenantCatalogueApiResponse,
  GetCustomTenantCatalogueUsageApiResponse,
  GetTrimLevelsApiArg,
  GetTrimLevelsApiResponse,
  RemapCustomMakeApiArg,
  RemapCustomModelFamilyApiArg,
  TrimLevelsResponseBodySchema,
} from './types';

export const vehicleCatalogueApi = omneticApi.injectEndpoints({
  overrideExisting: false,
  endpoints: (build) => ({
    getMakeModelWithMake: build.query<
      GetApiMakeModelWithMakeApiResponse,
      GetApiMakeModelWithMakeApiArg
    >({
      query: (queryArg) => {
        const URL_PREFIX = '/vehicle-catalogue/api/make-model';

        const url = match([isNotNilOrEmpty(queryArg.vehicleType), isNotNilOrEmpty(queryArg.make)])
          .with([true, true], always(`${URL_PREFIX}/${queryArg.vehicleType}/${queryArg.make}`))
          .with([true, false], always(`${URL_PREFIX}/${queryArg.vehicleType}`))
          .otherwise(always(`${URL_PREFIX}`));

        return {
          url,
          params: {lang: queryArg.lang, logo_url: queryArg.logoUrl},
        };
      },
    }),
    getMakeModelWithMakes: build.query<
      GetApiMakeModelWithMakeApiResponse,
      GetApiMakeModelsWithMakeApiArg
    >({
      async queryFn(queryArg, _queryApi, _extraOptions, fetchWithBaseQuery) {
        const results = await Promise.all(
          queryArg.makes.map((make) =>
            fetchWithBaseQuery({
              url: `/vehicle-catalogue/api/make-model/${queryArg.vehicleType}/${make}`,
              params: {lang: queryArg.lang},
            })
          )
        );

        const data = flatten(
          results.map((result) => result.data) as GetApiMakeModelWithMakeApiResponse[]
        );

        return {data};
      },
    }),
    getTrimLevels: build.query<
      GetTrimLevelsApiResponse,
      GetTrimLevelsApiArg | GetTrimLevelsApiArg[]
    >({
      // An empty model family returns the trims of the whole make; otherwise the catalogue lists
      // them per model family, so one request per family is merged here.
      async queryFn(queryArg, _queryApi, _extraOptions, fetchWithBaseQuery) {
        const selections = Array.isArray(queryArg) ? queryArg : [queryArg];

        const results = await Promise.all(
          selections.flatMap(({make, modelFamilies}) =>
            (isEmpty(modelFamilies) ? [''] : modelFamilies).map((modelFamily) =>
              fetchWithBaseQuery({
                url: '/vehicle-catalogue/api/trim_levels',
                params: {make, model_family: modelFamily},
              })
            )
          )
        );

        for (const result of results) {
          if (isNotNil(result.error)) {
            return {error: result.error};
          }
        }

        const parsedResponses = results.map((result) =>
          TrimLevelsResponseBodySchema.safeParse(result.data)
        );

        // A changed response shape must not pass as a make without trims.
        if (parsedResponses.some((parsedResponse) => !parsedResponse.success)) {
          return {
            error: {status: 'CUSTOM_ERROR', error: 'Unexpected trim levels response'} as const,
          };
        }

        const trimLevels = parsedResponses.flatMap((parsedResponse) =>
          parsedResponse.success ? (parsedResponse.data.trim_levels ?? []) : []
        );

        return {
          data: uniqBy(
            (trimLevel) => trimLevel.value,
            trimLevels.map((trimLevel) => ({value: trimLevel.const_key, label: trimLevel.label}))
          ),
        };
      },
    }),
    getAllMakesModels: build.query<GetAllMakeModelsApiResponse, GetAllMakeModelsApiArgs>({
      query: (queryArg) => ({
        url: `/vehicle-catalogue/api/make-model/${queryArg?.vehicleType}/MAKE_ALL`,
        params: isNotNil(queryArg) ? {lang: queryArg?.lang} : undefined,
      }),
      transformResponse: (response: GetApiMakeModelWithMakeApiResponse) => {
        const makes: Makes = {};
        const models: Models = {};
        const modelFamilies: ModelFamilies = {};

        response.forEach((make) => {
          const makeName = make.make;

          makes[makeName] = {
            name: makeName,
            label: make.default_label,
            models: make.models?.map((model) => model.model) ?? [],
            modelFamilies: [],
          };
          make.models?.forEach((model) => {
            const modelName = model.model;

            models[modelName] = {
              name: modelName,
              label: `${make.default_label} ${model.default_label}`,
              make: makeName,
              modelFamily: model.model_group?.group,
            };
            if (isNotNil(model.model_group)) {
              const familyName = model.model_group.group;

              if (!makes[makeName].modelFamilies.includes(familyName)) {
                makes[makeName].modelFamilies.push(familyName);
              }

              if (isNil(modelFamilies[familyName])) {
                modelFamilies[familyName] = {
                  name: familyName,
                  label: `${make.default_label} ${model.model_group.default_label}`,
                  make: makeName,
                  models: [modelName],
                };
              } else {
                modelFamilies[familyName].models.push(modelName);
              }
            }
          });
        });

        return {makes, models, modelFamilies};
      },
    }),
    getAllApiEnums: build.query<GetAllApiEnumsApiResponse, GetAllApiEnumsApiArg>({
      query: (queryArg) => ({
        url: `/vehicle-catalogue/api/enum`,
        params: {
          lang: queryArg.lang,
          order_by: queryArg.orderBy,
          vehicle_type: queryArg.vehicleType,
        },
      }),
    }),
    getCustomTenantCatalogue: build.query<GetCustomTenantCatalogueApiResponse, void>({
      query: () => ({
        url: `/dms/v1/vehicle/custom-tenant-catalogue`,
      }),
      providesTags: ['CustomTenantCatalogue'],
    }),
    getCustomTenantCatalogueUsage: build.query<GetCustomTenantCatalogueUsageApiResponse, void>({
      query: () => ({
        url: `/dms/v1/vehicle/custom-tenant-catalogue/usage`,
      }),
      providesTags: ['CustomTenantCatalogue'],
    }),
    remapCustomMake: build.mutation<void, RemapCustomMakeApiArg>({
      query: (body) => ({
        url: `/dms/v1/vehicle/custom-tenant-catalogue/remap-make`,
        method: 'POST',
        body,
      }),
      // No invalidatesTags: the endpoint answers 202, so refetching here returns the unchanged list.
      // useRemapProgress invalidates once the completion toast arrives.
    }),
    remapCustomModelFamily: build.mutation<void, RemapCustomModelFamilyApiArg>({
      query: (body) => ({
        url: `/dms/v1/vehicle/custom-tenant-catalogue/remap-model-family`,
        method: 'POST',
        body,
      }),
      // No invalidatesTags: the endpoint answers 202, so refetching here returns the unchanged list.
      // useRemapProgress invalidates once the completion toast arrives.
    }),
  }),
});

export const {
  useGetMakeModelWithMakeQuery,
  useLazyGetMakeModelWithMakeQuery,
  useGetMakeModelWithMakesQuery,
  useGetAllMakesModelsQuery,
  useGetTrimLevelsQuery,
  useLazyGetTrimLevelsQuery,
  useGetAllApiEnumsQuery,
  useGetCustomTenantCatalogueQuery,
  useLazyGetCustomTenantCatalogueQuery,
  useGetCustomTenantCatalogueUsageQuery,
  useRemapCustomMakeMutation,
  useRemapCustomModelFamilyMutation,
} = vehicleCatalogueApi;
